import React, { type FC, useCallback, useState } from 'react';

import { useIntl } from 'react-intl-next';

import { IconButton } from '@atlaskit/button/new';
import ThumbsDownIcon from '@atlaskit/icon/core/thumbs-down';
import ThumbUpIcon from '@atlaskit/icon/core/thumbs-up';
import { B300 } from '@atlaskit/theme/colors';
import { token } from '@atlaskit/tokens';

import { messages } from './messages';

const LikeIcon = () => <ThumbUpIcon label="" color={token('color.icon.subtlest')} />;

const ActiveLikeIcon = () => <ThumbUpIcon label="" color={token('color.icon.accent.blue', B300)} />;

const DislikeIcon = () => <ThumbsDownIcon label="" color={token('color.icon.subtlest')} />;

const ActiveDislikeIcon = () => (
	<ThumbsDownIcon label="" color={token('color.icon.accent.blue', B300)} />
);

type FeedbackButtonsProps = {
	liked: boolean | undefined;
	setLiked: (value: boolean) => void;
	setDisplayFlag: (value: boolean) => void;
	setFeedbackModal: (value: boolean) => void;
	onLikeButtonClickHandler?: (e: any) => void;
	onDislikeButtonClickHandler?: (e: any) => void;
};

// eslint-disable-next-line @typescript-eslint/ban-types
export const FeedbackButtons: FC<FeedbackButtonsProps> = ({
	liked,
	setLiked,
	setDisplayFlag,
	setFeedbackModal,
	onLikeButtonClickHandler,
	onDislikeButtonClickHandler,
}) => {
	const { formatMessage } = useIntl();
	const [likesDisabled, setLikesDisabled] = useState(false);

	// Using a single state to represent both like/dislike states
	// true - liked, false - disliked, undefined - neither are selected
	const likeButtonCallback = useCallback(
		(e: any) => {
			e.preventDefault();
			setDisplayFlag(true);
			setLiked(true);
			setLikesDisabled(true);
			onLikeButtonClickHandler && onLikeButtonClickHandler(e);
		},
		[setDisplayFlag, setLiked, onLikeButtonClickHandler],
	);

	const dislikeButtonCallback = useCallback(
		(e: any) => {
			e.preventDefault();
			setFeedbackModal(true);
			setLiked(false);
			setLikesDisabled(true);
			onDislikeButtonClickHandler && onDislikeButtonClickHandler(e);
		},
		[setFeedbackModal, setLiked, onDislikeButtonClickHandler],
	);

	return (
		<>
			<IconButton
				icon={liked === true ? ActiveLikeIcon : LikeIcon}
				onClick={likeButtonCallback}
				label={formatMessage(messages.ai_answer_component_like_button_tooltip)}
				appearance="subtle"
				isTooltipDisabled={false}
				isDisabled={liked && likesDisabled}
				testId="ai-answer-like-button"
				spacing="compact"
			/>
			<IconButton
				icon={liked === false ? ActiveDislikeIcon : DislikeIcon}
				onClick={dislikeButtonCallback}
				label={formatMessage(messages.ai_answer_component_dislike_button_tooltip)}
				appearance="subtle"
				isTooltipDisabled={false}
				isDisabled={!liked && likesDisabled}
				testId="ai-answer-dislike-button"
				spacing="compact"
			/>
		</>
	);
};
